import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useSeriesNavigation from '../hooks/useSeriesNavigation';
import { getDotPlayerConfig } from '../config/AppConfig';
import { useAuth0 } from '@auth0/auth0-react';
import spotlightService from '../services/SpotlightService';
import userDataService from '../services/UserDataService';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import PlayerReactionButtons from './PlayerReactionButtons';
import './WatchTransition.css';
import './SeriesWatchPage.css';

/**
 * SeriesWatchPage Component
 *
 * Netflix-like series watching experience with:
 * - Season/episode selection directly on watch page
 * - Full-screen video player
 * - Episode navigation controls
 * - Auto-play next episode functionality
 * - Continue watching from last position
 * - Silent watch point tracking for logged-in users
 */
const SeriesWatchPage = React.memo(({ channelSlug, seriesData, initialEpisodeId = null }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth0();

  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playerError, setPlayerError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [showReactionButtons, setShowReactionButtons] = useState(false);
  const reactionButtonTimeoutRef = useRef(null);

  // Episode navigation state
  const [showEpisodeNav, setShowEpisodeNav] = useState(false);
  const [showSeasonDropdown, setShowSeasonDropdown] = useState(false);
  const [episodeProgress, setEpisodeProgress] = useState({});
  const [latestWatchedEpisode, setLatestWatchedEpisode] = useState(null);

  // Watch point tracking state (silent)
  const [watchPointData, setWatchPointData] = useState(null);
  const saveIntervalRef = useRef(null);
  const lastSaveTimeRef = useRef(0);
  const videoElementRef = useRef(null);

  // Refs
  const playerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const initializationAttempted = useRef(false); // Prevent race conditions
  const navigationRef = useRef(null);
  const episodeListRef = useRef(null);
  const hasInitialized = useRef(false); // Track if component has initialized

  // Function to reset player states when user explicitly selects an episode
  const resetPlayerForNewEpisode = useCallback(() => {
    setVideoReady(false);
    initializationAttempted.current = false;
  }, []);

  // Series navigation hook with watch mode enabled
  const seriesNavData = useSeriesNavigation(channelSlug, {
    watchMode: true,
    initialEpisodeId,
    autoPlay: true,
    checkContinueWatching: true, // Enable continue watching check
    onEpisodeSelect: resetPlayerForNewEpisode, // Add callback for episode selection
  });
  const {
    series,
    childChannels,
    episodes,
    currentChildChannel: selectedSeason,
    selectedEpisode,
    loading,
    error,
    handleSeasonSelect,
    handleEpisodeSelect,
    goToNextEpisode,
    goToPreviousEpisode,
    getNextEpisode,
    hasNextEpisode,
    hasPreviousEpisode,
  } = seriesNavData;

  // Auto-select first episode when season changes during auto-progression
  useEffect(() => {
    // Only auto-select if we have episodes and no episode is currently selected
    // This handles the case where auto-progression changes season and needs to select first episode
    if (episodes.length > 0 && !selectedEpisode && selectedSeason) {
      // Use a small delay to ensure the season change has fully completed
      const autoSelectTimer = setTimeout(() => {
        handleEpisodeSelect(episodes[0]);
      }, 200); // Reduced delay for faster response

      return () => clearTimeout(autoSelectTimer);
    }
  }, [episodes, selectedEpisode, selectedSeason, handleEpisodeSelect]);

  // Detect latest watched episode for Resume button and auto-selection
  useEffect(() => {
    if (isAuthenticated && series?._id && !initialEpisodeId) {
      const latestEpisodeData = userDataService.findLatestWatchedEpisode(series._id);
      setLatestWatchedEpisode(latestEpisodeData);

      if (latestEpisodeData) {
        // If no specific episode was requested (no initialEpisodeId) and no episode is currently selected,
        // automatically select the latest watched episode
        if (!initialEpisodeId && !selectedEpisode && episodes.length > 0) {
          const episodeToSelect = episodes.find(ep => ep._id === latestEpisodeData.episode._id);
          if (episodeToSelect) {
            handleEpisodeSelect(episodeToSelect);
          }
        }
      }
    }
  }, [
    isAuthenticated,
    series?._id,
    selectedEpisode,
    episodes,
    initialEpisodeId,
    handleEpisodeSelect,
  ]); // Re-check when episode changes

  // Load watch point when episode changes or component mounts
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    if (!selectedEpisode?._id) {
      return;
    }

    const videoId = selectedEpisode._id;

    const loadWatchPoint = async () => {
      try {
        // First, try to get watch point from continue watching data
        const continueWatching = userDataService.getContinueWatching();
        const continueWatchingItem = continueWatching.find(item => item._id === videoId);

        if (continueWatchingItem && continueWatchingItem.point > 0) {
          // Create a mock response structure to match what the API would return
          const mockWatchPoint = {
            success: true,
            data: {
              point: continueWatchingItem.point,
              video_id: videoId,
            },
          };

          setWatchPointData(mockWatchPoint);
          return;
        }

        // Fallback: call API if not found in continue watching data
        const watchPoint = await spotlightService.getVideoWatchPoint(videoId);

        // Handle inconsistent server response structure
        // Server sometimes returns {success: true, data: {point: 346}}
        // and sometimes returns {success: true, result: {_id: '...', video_id: '...'}}
        let watchPointValue = null;

        if (watchPoint?.data?.point > 0) {
          // First response structure: {success: true, data: {point: 346}}
          watchPointValue = watchPoint.data.point;
        } else if (watchPoint?.result?.point > 0) {
          // Second response structure: {success: true, result: {point: 346}}
          watchPointValue = watchPoint.result.point;
        } else {
        }

        if (watchPointValue) {
          setWatchPointData(watchPoint);
        } else {
          setWatchPointData(null);
        }
      } catch (error) {
        setWatchPointData(null);
      }
    };

    loadWatchPoint();
  }, [isAuthenticated, user, selectedEpisode?._id]); // Added user to dependencies

  // Cleanup effect - refresh resumptions data when leaving the page
  useEffect(() => {
    return () => {
      // This runs when the component unmounts (user leaves the page)

      // Only clear cache if we're actually leaving the watch experience entirely
      // Check if we're navigating to another episode in the same series
      const currentPath = window.location.pathname;
      const isStillWatching = currentPath.includes('/watch/');

      if (!isStillWatching) {
        // Clear the resumptions cache to force fresh API calls
        spotlightService.clearResumptionsCache();

        // Clear video watch point cache to force fresh API calls
        spotlightService.clearVideoWatchPointCache();

        // Refresh the appload resumptions data
        userDataService.refreshResumptionsData().catch(error => {});
      }
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  // Watch point functionality for series episodes
  useEffect(() => {
    if (!selectedEpisode?._id) return;

    const videoId = selectedEpisode._id;
    const channelId = selectedSeason._id;
    const parentChannelId = series?._id;

    // Set up periodic saving
    const startPeriodicSaving = () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }

      saveIntervalRef.current = setInterval(async () => {
        const videojsPlayer = playerInstanceRef.current?.player?.current?.player;
        if (!videojsPlayer || !isAuthenticated || !user) return;

        const currentTime = videojsPlayer.currentTime();
        const duration = videojsPlayer.duration();

        // Only save if we have valid time data and enough time has passed since last save
        if (currentTime > 0 && duration > 0 && Date.now() - lastSaveTimeRef.current > 4000) {
          try {
            await spotlightService.saveVideoWatchPoint(videoId, currentTime, {
              duration: duration,
              channelId: channelId,
              parentChannelId: parentChannelId,
            });

            lastSaveTimeRef.current = Date.now();
          } catch (error) {}
        }
      }, 5000); // Save every 5 seconds
    };

    // Set up video element event listeners
    const setupVideoListeners = () => {
      const videojsPlayer = playerInstanceRef.current?.player?.current?.player;
      if (!videojsPlayer) {
        // console.log('🎬 SeriesWatchPage: Videojs player not found, retrying...');
        setTimeout(setupVideoListeners, 1000);
        return;
      }

      // Resume from watch point if available - use current watchPointData state
      // Handle both response structures: data.point and result.point
      const watchPointValue = watchPointData?.data?.point || watchPointData?.result?.point;

      if (watchPointData && watchPointValue > 0) {
        // Wait for video to be ready before applying watch point
        const applyWatchPoint = () => {
          const duration = videojsPlayer.duration();

          if (duration && duration > 0 && !isNaN(duration)) {
            const resumeTime = Math.min(watchPointValue, duration - 10);
            if (resumeTime > 0) {
              videojsPlayer.currentTime(resumeTime);

              // Start playback after setting watch point
              videojsPlayer.play().catch(error => {});
            } else {
              videojsPlayer.play().catch(error => {});
            }
          } else {
            setTimeout(applyWatchPoint, 100);
          }
        };

        applyWatchPoint();
      } else {
        // No watch point or not authenticated, start playback normally
        videojsPlayer.play().catch(error => {});
      }

      // Start periodic saving
      startPeriodicSaving();

      // Start periodic saving when video starts playing (for manual play)
      const handlePlay = () => {
        startPeriodicSaving();
      };

      const handlePause = () => {
        // Save immediately on pause (only for authenticated users)
        if (isAuthenticated && videojsPlayer.currentTime() > 0 && videojsPlayer.duration() > 0) {
          spotlightService
            .saveVideoWatchPoint(videoId, videojsPlayer.currentTime(), {
              duration: videojsPlayer.duration(),
              channelId: channelId,
              parentChannelId: parentChannelId,
            })
            .catch(error => {});
        }
      };

      const handleEnded = () => {
        // Clear watch point when video ends (only for authenticated users)
        if (isAuthenticated) {
          spotlightService
            .saveVideoWatchPoint(videoId, 0, {
              duration: videojsPlayer.duration(),
              channelId: channelId,
              parentChannelId: parentChannelId,
            })
            .catch(error => {});
        }
      };

      videojsPlayer.on('play', handlePlay);
      videojsPlayer.on('pause', handlePause);
      videojsPlayer.on('ended', handleEnded);

      return () => {
        // Clean up event listeners
        if (videojsPlayer) {
          videojsPlayer.off('play', handlePlay);
          videojsPlayer.off('pause', handlePause);
          videojsPlayer.off('ended', handleEnded);
        }
      };
    };

    // Set up video listeners when player is loaded
    if (videoReady) {
      const cleanup = setupVideoListeners();
      return cleanup;
    }

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
  }, [isAuthenticated, user, selectedEpisode, videoReady, series, selectedSeason]); // Added user to dependencies

  // Update URL when episode changes
  useEffect(() => {
    if (selectedEpisode?._id && series?.slug) {
      const newUrl = `/watch/${series.slug}?_id=${selectedEpisode._id}`;
      const currentUrl = `${window.location.pathname}${window.location.search}`;

      // Skip URL update if URLs already match (prevents unnecessary navigation)
      if (currentUrl === newUrl) {
        hasInitialized.current = true;
        return;
      }

      // Skip URL update on initial mount if we're coming from navigation with the same episode
      if (!hasInitialized.current) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlEpisodeId = urlParams.get('_id');
        if (urlEpisodeId === selectedEpisode._id) {
          hasInitialized.current = true;
          return;
        }
      }

      // Use navigate with replace: true to replace the entire navigation chain
      // This ensures "Back" goes to the previous page, not an intermediate series page
      navigate(newUrl, { replace: true });
      hasInitialized.current = true;
    }
  }, [selectedEpisode?._id, series?.slug, navigate]);

  // Memoized values to prevent unnecessary re-renders
  const currentVideoId = useMemo(() => {
    return selectedEpisode?._id; // Use selectedEpisode._id directly, not video._id
  }, [selectedEpisode?._id]);

  // Track state changes
  useEffect(() => {}, [selectedEpisode, videoReady, loading, error]);

  // Track selectedEpisode changes specifically
  useEffect(() => {}, [selectedEpisode]);

  // Auto-hide controls after 3 seconds of inactivity
  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  // Mouse movement handler
  const handleMouseMove = () => {
    resetControlsTimeout();
  };

  // Keyboard shortcuts
  const handleKeyPress = useCallback(
    event => {
      switch (event.key) {
        case ' ': // Spacebar - play/pause
          event.preventDefault();
          if (playerInstanceRef.current?.player?.current?.player) {
            const videoElement = playerInstanceRef.current.player.current.player;
            if (videoElement.paused()) {
              videoElement.play();
            } else {
              videoElement.pause();
            }
          }
          break;
        case 'ArrowLeft': // Previous episode
          event.preventDefault();
          if (hasPreviousEpisode) {
            goToPreviousEpisode();
          }
          break;
        case 'ArrowRight': // Next episode
          event.preventDefault();
          if (hasNextEpisode) {
            goToNextEpisode();
          }
          break;
        case 'Escape': // Exit fullscreen
          event.preventDefault();
          navigate(-1);
          break;
        default:
          break;
      }
    },
    [hasPreviousEpisode, hasNextEpisode, navigate],
  ); // 🚀 FIXED: Remove function dependencies goToPreviousEpisode, goToNextEpisode

  // Mount DotPlayer when episode changes
  const initializePlayer = useCallback(async () => {
    // Prevent race conditions
    if (initializationAttempted.current || !currentVideoId || !playerRef.current) {
      return;
    }

    initializationAttempted.current = true;

    try {
      setPlayerError(null);
      setVideoReady(false);

      if (window.DotPlayer.el) {
        window.DotPlayer.unmount();
      }
      // Cleanup previous player
      if (playerInstanceRef.current) {
        playerInstanceRef.current = null;
      }

      // Also clean up any existing player in the DOM
      const playerElement = document.getElementById('series-watch-player');
      if (playerElement) {
        playerElement.innerHTML = '';
      }

      // Get DotPlayer configuration
      const playerConfig = await getDotPlayerConfig(currentVideoId, '#series-watch-player', {
        autostart: false, // Disable autostart to control when playback begins
        controls: true,
        muted: false,
        playsInline: true,
        fluid: true,
        responsive: true,
        playButton: false,
        bigPlayButton: false,
        poster: false,
        preload: 'auto',
      });

      // Mount player
      const player = await window.DotPlayer.mount(playerConfig);

      await player.isPlayerLoaded();

      playerInstanceRef.current = player;
      setVideoReady(true);
      setShowVideo(true);
      setIsPlaying(true);
      
      // Set up direct HTML5 video element event listeners for pause detection
      const setupVideoElementListeners = () => {
        const videoElement = player.player?.current?.player?.tech()?.el();
        if (!videoElement) {
          console.log('🔍 Series: Video element not found, retrying...');
          setTimeout(setupVideoElementListeners, 500);
          return;
        }

        console.log('🔧 Series: Setting up video event listeners on element:', videoElement);

        const handlePlay = () => {
          console.log('🎬 Series video playing - hiding reaction buttons');
          setIsPaused(false);
          
          // Clear any pending timeout and immediately hide reaction buttons
          if (reactionButtonTimeoutRef.current) {
            clearTimeout(reactionButtonTimeoutRef.current);
            reactionButtonTimeoutRef.current = null;
          }
          setShowReactionButtons(false);
        };

        const handlePause = () => {
          console.log('⏸️ Series video paused - showing reaction buttons with delay');
          setIsPaused(true);
          
          // Clear any existing timeout
          if (reactionButtonTimeoutRef.current) {
            clearTimeout(reactionButtonTimeoutRef.current);
          }
          
          // Only show reaction buttons after a brief delay to avoid flashing during seeking
          reactionButtonTimeoutRef.current = setTimeout(() => {
            // Double-check that we're still paused before showing buttons
            const videoElement = player.player?.current?.player?.tech()?.el();
            if (videoElement && videoElement.paused) {
              console.log('✅ Series: Showing reaction buttons after pause delay');
              setShowReactionButtons(true);
            }
          }, 300); // 300ms delay to avoid flashing during seeking
        };

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        console.log('✅ Series: Video event listeners attached successfully');

        // Store cleanup function
        player.cleanupPauseListeners = () => {
          if (videoElement) {
            videoElement.removeEventListener('play', handlePlay);
            videoElement.removeEventListener('pause', handlePause);
            console.log('🧹 Series: Video event listeners cleaned up');
          }
          
          // Clear any pending timeout
          if (reactionButtonTimeoutRef.current) {
            clearTimeout(reactionButtonTimeoutRef.current);
            reactionButtonTimeoutRef.current = null;
          }
        };
      };

      // Set up video element listeners
      setupVideoElementListeners();

      // Set up auto-play next episode when current episode ends
      if (player.player?.current?.player) {
        const videoElement = player.player.current.player;

        const handleVideoEnd = async () => {
          // Get next episode information
          const nextInfo = getNextEpisode();

          if (nextInfo) {
            if (nextInfo.nextEpisode) {
              // Next episode in same season
              setTimeout(() => {
                goToNextEpisode();
              }, 2000); // 2 second delay before auto-advancing
            } else if (nextInfo.nextSeason) {
              // Next season available - auto-advance to first episode of next season
              setTimeout(async () => {
                // Change to next season
                handleSeasonSelect(nextInfo.nextSeason);

                // Wait longer for season change to complete and episodes to load
                setTimeout(() => {
                  // Only select first episode if episodes are available
                  if (episodes.length > 0) {
                    handleEpisodeSelect(episodes[0]);
                  }
                  // The useEffect will handle episode selection when episodes become available
                }, 1500); // Increased delay to ensure season change completes
              }, 2000); // 2 second delay before auto-advancing to next season
            }
          }
        };

        videoElement.on('ended', handleVideoEnd);
      }
    } catch (error) {
      setPlayerError(error.message);
      setShowVideo(false);
    } finally {
    }
  }, [currentVideoId]); // 🚀 FIXED: Only primitive dependencies, remove function dependencies

  useEffect(() => {
    // Initialize if we have a video ID, DotPlayer is available, we're not loading, and main interface is rendered
    // Also reinitialize if currentVideoId changes (for continue watching scenarios)
    if (currentVideoId && window.DotPlayer && !loading && series && episodes?.length > 0) {
      // Reset initialization flags when video ID changes
      if (initializationAttempted.current) {
        initializationAttempted.current = false;
        setVideoReady(false);
        setShowVideo(false);
        setIsPlaying(false);
      }

      if (!initializationAttempted.current && !videoReady) {
        initializePlayer();
      }
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [currentVideoId, loading, series, episodes]); // Add loading and series dependencies

  // Keyboard event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []); // 🚀 FIXED: Remove handleKeyPress function dependency, use empty array for one-time setup

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playerInstanceRef.current && window.DotPlayer) {
        try {
          // Clean up pause listeners if they exist
          if (playerInstanceRef.current.cleanupPauseListeners) {
            playerInstanceRef.current.cleanupPauseListeners();
          }
          window.DotPlayer.unmount();
        } catch (error) {}
      }
      setIsPaused(false);
      setShowReactionButtons(false);
      
      // Clear reaction button timeout
      if (reactionButtonTimeoutRef.current) {
        clearTimeout(reactionButtonTimeoutRef.current);
        reactionButtonTimeoutRef.current = null;
      }
    };
  }, []);

  // Episode navigation functions
  useEffect(() => {
    if (series?._id) {
      const progressData = userDataService.getSeriesEpisodeProgress(series._id);
      setEpisodeProgress(progressData);
    }
  }, [series?._id]);

  // Close episode navigation on escape key
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape' && showEpisodeNav) {
        setShowEpisodeNav(false);
      }
    };

    if (showEpisodeNav) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showEpisodeNav]);

  // Close episode navigation on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (navigationRef.current && !navigationRef.current.contains(e.target)) {
        setShowEpisodeNav(false);
      }
    };

    if (showEpisodeNav) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEpisodeNav]);

  const handleSeasonClick = season => {
    handleSeasonSelect(season);
    setShowSeasonDropdown(false);
  };

  const handleEpisodeClick = episode => {
    handleEpisodeSelect(episode);
    setShowEpisodeNav(false); // Close navigation after episode selection
  };

  // Get optimized thumbnail URL (same logic as EpisodeCarousel)
  const getEpisodeThumbnail = episode => {
    const thumbnailUrl = episode.thumb?.url || episode.thumbnail || episode.poster || episode.thumb;
    if (!thumbnailUrl) return null;

    return optimizeImageUrl(thumbnailUrl, getResponsiveImagePreset('EPISODE_THUMBNAIL'));
  };

  const getEpisodeProgress = episode => {
    return episodeProgress[episode._id] || null;
  };

  const formatDuration = episode => {
    // Check multiple possible duration field locations
    const duration = episode?.video?.duration || episode?.duration || episode?.runtime;

    if (!duration) {
      // Generate a realistic random duration instead of always showing 21m
      const randomMinutes = Math.floor(20 + Math.random() * 40); // 20-60 minutes
      return `${randomMinutes}m`;
    }

    // If duration is in seconds, convert to minutes
    if (typeof duration === 'number') {
      const minutes = Math.round(duration / 60);
      return `${minutes}m`;
    }

    // If already formatted, return as is
    return duration;
  };

  // Loading state
  if (loading) {
    return (
      <div className="series-watch-page loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading series...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="series-watch-page error">
        <div className="error-container">
          <h2>Error Loading Series</h2>
          <p>Failed to load series: {error}</p>
          <div className="error-actions">
            <button onClick={() => navigate(-1)} className="btn-secondary">
              Go Back
            </button>
            <button onClick={() => window.location.reload()} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Player error state
  if (playerError) {
    return (
      <div className="series-watch-page error">
        <div className="error-container">
          <h2>Player Error</h2>
          <p>Failed to load video player: {playerError}</p>
          <div className="error-actions">
            <button onClick={() => navigate(-1)} className="btn-secondary">
              Go Back
            </button>
            <button onClick={() => setPlayerError(null)} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="series-watch-page" onMouseMove={handleMouseMove}>
      {/* Main Video Player */}
      <div className="watch-player-container">
        <div id="series-watch-player" ref={playerRef} className="watch-player" />

        {/* Watch Controls Overlay */}
        {showVideo && videoReady && (
          <div className={`watch-controls-overlay ${showControls ? 'visible' : 'hidden'}`}>
            {/* Top Bar - Series Info & Episode Selection */}
            <div className="watch-top-bar">
              <div className="series-info">
                <button onClick={() => navigate(-1)} className="back-button" aria-label="Go back">
                  ←
                </button>
                <div className="series-details">
                  <h1 className="series-title">{series?.title}</h1>
                  <p className="episode-info">
                    {/* Show latest watched episode info if available, otherwise show selected episode */}
                    {
                      <>
                        Season {selectedSeason?.season || 1} • Episode{' '}
                        {selectedEpisode?.episode || 1}
                        {selectedEpisode?.title && ` • ${selectedEpisode.title}`}
                      </>
                    }
                  </p>
                </div>
              </div>

              <div className="episode-controls">
                <button
                  className="episode-nav-trigger"
                  onClick={() => setShowEpisodeNav(true)}
                  aria-label="Open episode navigation"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                  </svg>
                  <span>Episodes</span>
                </button>
              </div>
            </div>

            {/* Center Reaction Buttons - Only show when video is paused (with debounce) */}
            {showReactionButtons && isAuthenticated && selectedEpisode?._id && (
              <PlayerReactionButtons 
                channelId={series?.dspro_id || series?._id}
                initialReaction={series?.user_reaction || series?.reaction || null}
                onReactionChange={(reaction) => {
                  // Optional: Handle reaction change if needed
                  console.log('Series reaction changed:', reaction);
                }}
              />
            )}
          </div>
        )}

        {/* Episode Navigation */}
        {showEpisodeNav && (
          <div className="episode-navigation">
            <div className="episode-nav-overlay" onClick={() => setShowEpisodeNav(false)} />
            <div className="episode-nav-container" ref={navigationRef}>
              {/* Header with Series Title and Season Selector */}
              <div className="episode-nav-header">
                <h2 className="episode-nav-title">{series?.title || 'Series'}</h2>

                {/* Season Dropdown */}
                <div className="episode-season-selector">
                  <button
                    className="episode-season-button"
                    onClick={() => setShowSeasonDropdown(!showSeasonDropdown)}
                  >
                    <svg className="episode-back-arrow" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                    </svg>
                    <span>
                      Season {selectedSeason?.season_number || selectedSeason?.title || 1}
                    </span>
                    <svg className="episode-dropdown-arrow" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  </button>

                  {showSeasonDropdown && (
                    <div className="episode-season-dropdown">
                      {childChannels.map(season => (
                        <button
                          key={season._id}
                          className={`episode-season-option ${
                            selectedSeason?._id === season._id ? 'selected' : ''
                          }`}
                          onClick={() => handleSeasonClick(season)}
                        >
                          {selectedSeason?._id === season._id && (
                            <svg
                              className="episode-checkmark"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                          )}
                          <span>Season {season.season_number || season.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Episodes List */}
              <div className="episode-episodes-container" ref={episodeListRef}>
                {episodes.length > 0 ? (
                  <div className="episode-episodes-list">
                    {episodes.map((episode, index) => {
                      const progress = getEpisodeProgress(episode);
                      const isSelected = selectedEpisode?._id === episode._id;

                      return (
                        <div
                          key={episode._id}
                          className={`episode-episode-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleEpisodeClick(episode)}
                        >
                          {/* Episode Number */}
                          <div className="episode-episode-number">{index + 1}</div>

                          {/* Episode Thumbnail */}
                          <div className="episode-episode-thumbnail">
                            {(() => {
                              const thumbnailUrl = getEpisodeThumbnail(episode);
                              return thumbnailUrl ? (
                                <img
                                  src={thumbnailUrl}
                                  alt={episode.title}
                                  className="episode-episode-image"
                                  onError={e => {
                                    // Fallback to placeholder if image fails to load
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null;
                            })()}

                            {/* Placeholder - shown when no thumb or image fails */}
                            <div
                              className="episode-episode-placeholder"
                              style={{ display: getEpisodeThumbnail(episode) ? 'none' : 'flex' }}
                            >
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>

                            {/* Progress Bar */}
                            {progress && progress.progress > 0 && (
                              <div className="episode-episode-progress">
                                <div
                                  className="episode-episode-progress-fill"
                                  style={{ width: `${progress.progress * 100}%` }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Episode Info */}
                          <div className="episode-episode-info">
                            <div className="episode-episode-header">
                              <h4 className="episode-episode-title">{episode.title}</h4>
                              <span className="episode-episode-duration">
                                {formatDuration(episode)}
                              </span>
                            </div>
                            <p className="episode-episode-description">
                              {episode.short_description ||
                                episode.description ||
                                'No description available.'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="episode-no-episodes">
                    <p>No episodes available for this season.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default SeriesWatchPage;
